import { authenticatedFetch } from "@lumi/data";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";

/**
 * Title data structure for editing
 */
export interface TitleData {
  id: string;
  slug: string;
  name: string;
  year?: number;
  runtimeMinutes?: number;
  synopsis?: string;
  description?: string; // Extended description
  rating?: string; // Content rating (G, PG, PG-13, R, etc.)
  country?: string;
  directorName?: string;
  castNames?: string[];
  genres?: string[];
  posterUrlHd?: string;
  backdropUrlHd?: string;
  logoUrl?: string;
  horizontalTileUrl?: string;
  verticalTileUrl?: string;
  languages?: string[];
  resolution?: string;
  castData?: unknown;
  crewData?: unknown;
  published: boolean;
  assets?: Array<{
    id: string;
    kind: "FEATURE" | "TRAILER";
    hlsUrl: string;
    encodingStatus?: string | null;
    rawVideoKey?: string | null;
    // R2 metadata fields
    originalFileName?: string | null;
    uploadedAt?: string | null;
    uploadedBy?: string | null;
    fileSize?: bigint | null;
    contentType?: string | null;
    // Thumbnail sprite data
    thumbnailVttUrl?: string | null;
    durationSec?: number | null;
    subtitles?: Array<{
      id: string;
      lang: string;
      format: string;
      url: string;
      forced: boolean;
    }>;
  }>;
}

/**
 * Hook to fetch a single title by ID
 */
export function useTitle(titleId: string | null) {
  const { accessToken, refreshAuth } = useAuth();
  const [data, setData] = useState<TitleData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTitle = useCallback(async () => {
    console.log("[useTitle] fetchTitle called:", { titleId, hasToken: !!accessToken });

    if (!titleId || !accessToken) {
      setData(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";
      const url = `${baseUrl}/v1/admin/titles/${titleId}`;
      console.log("[useTitle] Fetching from:", url);

      const titleData = await authenticatedFetch<TitleData>(
        url,
        { method: "GET" },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      console.log("[useTitle] Received title data:", {
        id: titleData.id,
        name: titleData.name,
        assetsCount: titleData.assets?.length || 0,
        assets: titleData.assets,
      });

      setData(titleData);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to fetch title";
      setError(message);
      console.error("Error fetching title:", err);
    } finally {
      setLoading(false);
    }
  }, [titleId, accessToken, refreshAuth]);

  // Fetch on mount and when titleId changes
  useEffect(() => {
    void fetchTitle();
  }, [fetchTitle]);

  // Wrap refetch in useCallback to stabilize its reference
  const refetch = useCallback(() => {
    return fetchTitle();
  }, [fetchTitle]);

  return { data, loading, error, refetch };
}

/**
 * Hook to update a title
 */
export function useUpdateTitle() {
  const { accessToken, refreshAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateTitle = useCallback(
    async (titleId: string, updates: Partial<TitleData>) => {
      if (!accessToken) {
        throw new Error("Not authenticated");
      }

      setLoading(true);
      setError(null);

      try {
        const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";
        const updatedTitle = await authenticatedFetch<TitleData>(
          `${baseUrl}/v1/admin/titles/${titleId}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updates),
          },
          accessToken,
          createRefreshCallback(refreshAuth),
        );

        return updatedTitle;
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to update title";
        setError(message);
        console.error("Error updating title:", err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [accessToken, refreshAuth],
  );

  return { updateTitle, loading, error };
}

/**
 * Combined hook for title CRUD operations
 */
export function useTitleMutations(titleId: string | null) {
  const { data, loading: fetchLoading, error: fetchError, refetch } = useTitle(titleId);
  const { updateTitle, loading: updateLoading, error: updateError } = useUpdateTitle();

  return {
    title: data,
    loading: fetchLoading || updateLoading,
    error: fetchError || updateError,
    refetch,
    updateTitle,
  };
}
